import { DocumentationChapter } from 'api/documentation';

export interface DocumentationSection {
  chapterSlug: string;
  chapterPath: string;
  chapterTitle: string;
  heading: string;
  anchor: string;
  body: string;
}

export interface DocumentationAnchor {
  chapterPath: string;
  anchor: string;
}

export interface DocumentationTreeNode {
  chapter: DocumentationChapter;
  children: DocumentationTreeNode[];
}

export const DOCUMENTATION_ROOT = '/documentation';
/** Matches the Word guides; Arial stays easy to read over long text. */
export const DOCUMENTATION_FONT_FAMILY = 'Arial, Helvetica, sans-serif';
export const DOCUMENTATION_ASSET_PATTERN = /^assets\/([0-9a-f]+\.[a-z0-9]+)$/;

export const markdownText = (node: any): string => {
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(markdownText).join('');
  return node?.props ? markdownText(node.props.children) : '';
};

export const slugifyHeading = (heading: string): string =>
  heading
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

// Older documentation responses only have a flat slug.
export const chapterPath = (chapter: DocumentationChapter): string =>
  chapter.path ?? chapter.slug;

export const chapterUrl = (
  chapter: DocumentationChapter,
  anchor?: string,
): string =>
  `${DOCUMENTATION_ROOT}/${chapterPath(chapter)}${anchor ? `#${anchor}` : ''}`;

/**
 * Ids for the headings of a chapter, in document order. Schema v2 responses
 * carry them (including author-chosen ids); older ones are derived from text.
 */
export const createHeadingIdGenerator = (chapter?: DocumentationChapter) => {
  const counts = new Map<string, number>();
  let index = 0;
  return (text: string): string => {
    const provided = chapter?.headings?.[index++]?.id;
    if (provided) return provided;
    const base = slugifyHeading(text);
    const count = counts.get(base) ?? 0;
    counts.set(base, count + 1);
    return count ? `${base}-${count}` : base;
  };
};

const headings = (chapter: DocumentationChapter) => {
  const result: Array<{ title: string; anchor: string; start: number }> = [];
  const nextId = createHeadingIdGenerator(chapter);
  const pattern = /^(?:(`{3,}|~{3,}).*|#{1,6}\s+(.+?)\s*)$/gm;
  let fence: string | undefined;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(chapter.markdown)) !== null) {
    if (match[1]) {
      if (!fence) fence = match[1][0];
      else if (match[1][0] === fence) fence = undefined;
      continue;
    }
    if (fence) continue;
    result.push({
      title: match[2].replace(/^\d+\.\s+/, '').trim(),
      anchor: nextId(match[2]),
      start: match.index,
    });
  }
  return result;
};

/**
 * Heading ids keyed by the heading's 1-based line in the Markdown. Rendering
 * looks ids up by position rather than counting headings, so re-renders
 * (e.g. React StrictMode) cannot shift them.
 */
export const headingIdsByLine = (
  chapter: DocumentationChapter,
): Map<number, string> =>
  new Map(
    headings(chapter).map(heading => [
      chapter.markdown.slice(0, heading.start).split('\n').length,
      heading.anchor,
    ]),
  );

export const buildDocumentationSections = (
  chapters: DocumentationChapter[],
): DocumentationSection[] =>
  chapters.flatMap(chapter => {
    const chapterHeadings = headings(chapter);
    return chapterHeadings.map((heading, index) => ({
      chapterSlug: chapter.slug,
      chapterPath: chapterPath(chapter),
      chapterTitle: chapter.title,
      heading: heading.title,
      anchor: heading.anchor,
      body: chapter.markdown
        .slice(
          heading.start,
          chapterHeadings[index + 1]?.start ?? chapter.markdown.length,
        )
        .replace(/^#{1,6}\s+.+$/m, '')
        .trim(),
    }));
  });

export const buildAnchorMap = (
  sections: DocumentationSection[],
): Record<string, DocumentationAnchor> =>
  sections.reduce<Record<string, DocumentationAnchor>>((anchors, section) => {
    if (!anchors[section.anchor]) {
      anchors[section.anchor] = {
        chapterPath: section.chapterPath,
        anchor: section.anchor,
      };
    }
    return anchors;
  }, {});

export const buildDocumentationTree = (
  chapters: DocumentationChapter[],
): DocumentationTreeNode[] => {
  const nodes = new Map<string, DocumentationTreeNode>();
  chapters.forEach(chapter =>
    nodes.set(chapterPath(chapter), { chapter, children: [] }),
  );
  const roots: DocumentationTreeNode[] = [];
  chapters.forEach(chapter => {
    const node = nodes.get(chapterPath(chapter))!;
    const parent = chapter.parent ? nodes.get(chapter.parent) : undefined;
    (parent ? parent.children : roots).push(node);
  });
  return roots;
};

export interface DocumentationNavGroup {
  label?: string;
  nodes: DocumentationTreeNode[];
}

/** Consecutive top-level pages that share a `group` form one nav section. */
export const groupDocumentationTree = (
  tree: DocumentationTreeNode[],
): DocumentationNavGroup[] =>
  tree.reduce<DocumentationNavGroup[]>((groups, node) => {
    const label = node.chapter.group ?? undefined;
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.nodes.push(node);
    else groups.push({ label, nodes: [node] });
    return groups;
  }, []);

/** Paths of the chapter and all of its ancestors. */
export const chapterLineage = (
  chapters: DocumentationChapter[],
  chapter?: DocumentationChapter,
): Set<string> => {
  const byPath = new Map(chapters.map(item => [chapterPath(item), item]));
  const lineage = new Set<string>();
  let current = chapter;
  while (current && !lineage.has(chapterPath(current))) {
    lineage.add(chapterPath(current));
    current = current.parent ? byPath.get(current.parent) : undefined;
  }
  return lineage;
};

export const getDocumentationPath = (pathname: string): string | undefined => {
  const segments = pathname.split('/').filter(Boolean);
  const documentationIndex = segments.indexOf('documentation');
  if (documentationIndex === -1) return undefined;
  const remaining = segments.slice(documentationIndex + 1);
  return remaining.length
    ? remaining.map(decodeURIComponent).join('/')
    : undefined;
};

/** Resolve a URL to a chapter by full path, falling back to legacy slugs. */
export const findDocumentationChapter = (
  chapters: DocumentationChapter[],
  pathname: string,
): DocumentationChapter | undefined => {
  const path = getDocumentationPath(pathname);
  if (!path) return undefined;
  const lastSegment = path.split('/').pop();
  return (
    chapters.find(chapter => chapterPath(chapter) === path) ??
    chapters.find(chapter => chapter.slug === path.replace(/\//g, '-')) ??
    chapters.find(chapter => chapter.slug === lastSegment)
  );
};

/** An in-app (non-documentation) route that should render as a call to action. */
export const isAppRouteHref = (href?: string): boolean =>
  !!href &&
  href.startsWith('/') &&
  !href.startsWith('//') &&
  href !== DOCUMENTATION_ROOT &&
  !href.startsWith(`${DOCUMENTATION_ROOT}/`) &&
  !href.startsWith(`${DOCUMENTATION_ROOT}#`);

export const formatReviewedDate = (value: string): string => {
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
};

/** Smooth-scroll to a heading on the current page; false if it isn't there. */
export const scrollToHeading = (id: string): boolean => {
  const element = document.getElementById(id);
  element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  return !!element;
};

/**
 * Carry the current query (environmentId, organization) onto an in-app link.
 * Without it the app re-adds environmentId with a replace that drops the
 * #section, which sends the reader back to the top of the page.
 */
export const keepSearch = (to: string, search: string): string => {
  if (!search || search === '?' || to.includes('?')) return to;
  const hashAt = to.indexOf('#');
  return hashAt === -1
    ? `${to}${search}`
    : `${to.slice(0, hashAt)}${search}${to.slice(hashAt)}`;
};

/**
 * The URL for an in-app link opened in a new tab. A new tab has none of this
 * tab's sessionStorage, so the organization (the sign-in realm) and the
 * current environment travel in the query; the link's own params win.
 */
export const appLinkHref = (
  to: string,
  search: string,
  organization: string,
): string => {
  const url = new URL(to, 'http://app');
  const current = new URLSearchParams(search);
  if (organization) current.set('organization', organization);
  current.forEach((value, key) => {
    if (!url.searchParams.has(key)) url.searchParams.set(key, value);
  });
  return `${url.pathname}${url.search}${url.hash}`;
};
